import { DateTime } from "luxon"
import type { TMealTime } from "./types"

type TTimeRuleDef = {
  operationId: string
  description: string
  daysOffset: number
  time: string
  action: "open" | "close"
  userRole: string
  mealTime: TMealTime
}

export class TimeRule {
  operationId: TTimeRuleDef["operationId"]
  description: TTimeRuleDef["description"]
  daysOffset: TTimeRuleDef["daysOffset"]
  time: TTimeRuleDef["time"]
  action: TTimeRuleDef["action"]
  userRole: TTimeRuleDef["userRole"]
  mealTime: TTimeRuleDef["mealTime"]

  constructor(timeRuleDef: TTimeRuleDef) {
    this.operationId = timeRuleDef.operationId
    this.description = timeRuleDef.description
    this.daysOffset = timeRuleDef.daysOffset
    this.time = timeRuleDef.time
    this.action = timeRuleDef.action
    this.userRole = timeRuleDef.userRole
    this.mealTime = timeRuleDef.mealTime
  }

  /**
   * For a given operation and app time, we want to know if that operation is allowed at that app time.
   * Note that our operation time rules only care about the time part of a date time.
   * Commonly the input date is the order item's OrderDate as a date time.
   * For example:
   * Say the app time is 2025-02-10T18:20:00 and the operation in question is 'cancel'.
   * Say the input date time is 2025-02-11T00:00:00.
   * We grab the time rule for the 'cancel' operation which says you can only cancel up until 16:00:00 the day before.
   * For our example that results in 2025-02-10T16:00:00 so in this case the operation would not be allowed.
   * @param appDt The DateTime object representing the current app date time
   * @param inputDt The DateTime object for an entity (order, etc) - and we want to check if the operation indicated by the time rule is allowed on the entity.

   * @returns boolean
   *
   * @example timeRule.isSatisfied("2025-02-10T18:20:00" as DateTime, "2025-02-11T00:00:00" as DateTime)
   * // returns false
   *
   */
  isSatisfied(appDt: DateTime<true>, inputDt: DateTime<true>): boolean {
    // This is why we do not care about the time part of input date time so we explicitly set it to midnight here
    // just to indicate that the time part of the passed input date is irrelevant.
    const inputDtMidnight = inputDt.startOf("day")
    const ruleDt = this._getRuleDt(inputDtMidnight)

    switch (this.action) {
      case "close": {
        const isBeforeClose = appDt < ruleDt
        return isBeforeClose
      }
      case "open": {
        const isAfterOpen = appDt > ruleDt
        return isAfterOpen
      }
    }
  }

  // Helper function
  _getRuleDt(inputDt: DateTime<true>): DateTime<true> {
    // Note that time rules don't really care about the time zone. There is no such property on a time rule.
    // Rather, the input date times being tested against time rule already have the appropriate time zone on them.
    // Note, DateTime.fromISO with only time will give us a datetime whose date is today, which we can ignore,
    // but whose time is, crucially, the passed time.
    const timeDt = DateTime.fromISO(this.time)
    const ruleDt = inputDt
      .set({
        hour: timeDt.hour,
        minute: timeDt.minute,
        second: timeDt.second,
      })
      .plus({ days: this.daysOffset })

    return ruleDt
  }
}
